import _ from "lodash";
/** external */
import React, {
  useState,
  useEffect,
  memo,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import { elevations, PausedAssignmentIllustration } from "@toddle-edu/ds-theme";
import { createPortal } from "react-dom";
import { EmptyState, IconButton, Modal } from "@toddle-edu/ds-web";
import { withRouter } from "react-router";
import withLoader from "UIComponents/GqlWithLoader";
import PropTypes from "prop-types";
import classNames from "classnames";
import { ChevronRightOutlined } from "@toddle-edu/ds-icons";
import { Detector } from "react-detect-offline";

/** internal */
import CompactLEPreview from "AppComponents/CompactLEPreview";
import { useI18n } from "Hooks";
import HeaderV2 from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/routes/ClassRoomDetails/components/TeacherClassRoomDetails/components/HeaderV2";
import {
  getIsUserOnline,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import {
  getUserInfo,
  setGlobalTrayEnabledState,
} from "Login/modules/LoginModule";
import {
  MODE_TEACHER_PREVIEW,
  MODE_STUDENT_ATTEMPT,
  MODE_STUDENT_VIEW,
  FIELD_UID_TITLE,
  WORKSHEET_ELEMENT_IDS,
} from "Worksheet/modules/WorksheetConstants";
import { useDidUpdate, useDrag } from "Utils/CustomHooks";
import {
  getScoreValueElementRatings,
  registerTimeTrackingEventInLocalStorage,
  getIsStudentAssignmentRevampEnabled,
  getIsEvaluationApplicable,
} from "ClassRoom/modules/utils";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import {
  getStudentAssignmentDetailsEnhancer,
  getStudentQuestionDetailsEnhancer,
  getStudentAssignmentEnhancer,
  getStudentQuestionDetailsEnhancerV2,
} from "Worksheet/modules/WorksheetEnhancers";
import { getAssignmentDeletedStatus } from "ClassRoom/modules/ClassRoomModule";
import { getDateInUtc } from "Utils/DateTimeHelpers";
import { onEnterOrSpaceKeyDown, isMobileByUserAgent } from "Utils";
import { isSafeExamBrowser } from "Utils/DeviceDetectHelpers";
import SectionFeed from "../SectionFeed/SectionFeed";
import Calculator from "UIComponents/Calculator";
import { goToBack } from "modules/NavigationModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import withPresenceChannel from "UIComponents/PresenceChannelHoc";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import {
  getDeviceInfo,
  sendWorksheetMonitoringEvent,
} from "WorksheetMonitoring/modules/WorksheetMonitoringUtils";
import UIModal from "UIComponents/UIModal";
import { getStudentWorksheetDataFromEvents } from "Worksheet/components/WorksheetTimer/WorksheetTimerUtils";

/** relative */
import classes from "./StudentWorksheetSubmission.scss";
import Header from "./Header";
import {
  getSideBarOptions,
  getAssignmentData,
  isWorksheetWebView,
  sendMessageToParentApp,
  getSideBarOptionsV2,
  isStudentEndMode,
  dispatchStudentMonitoringEventToast,
} from "../../modules/WorksheetUtils";
import { getIsStudentCanCopyPasteInWorksheet } from "../../modules/WorksheetFeatureFlags";
import {
  updateActiveQuestion,
  createEntityEventTracking,
} from "../../modules/WorksheetModules";
import SideBar from "../SideBar";
import StudentWorksheetInstructions from "./StudentWorksheetInstructions";
import StudentAssignmentMessaging from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/routes/ClassRoomDetails/routes/StudentAssignment/components/StudentAssignmentMessaging";
import { useDisableClipboard } from "./useDispatchClipboard";

/**
 *
 * @returns PauseWorksheetModal component displayed when worksheet is paused
 */
const PausedWorksheetModal = ({ isMessagePaneActive = false }) => {
  const { t } = useI18n();
  const overlayClasses = classNames({
    [classes.pausedWorksheetOverlay]: true,
    [classes.pausedWorksheetOverlayWhenMsgPaneActive]: isMessagePaneActive,
  });

  return (
    <UIModal
      isOpen={true}
      overlayContainer={overlayClasses}
      modalContent={classes.pausedWorksheetContent}
    >
      <div className={classes.pausedWorksheetHeader}>
        <h6 style={{ display: "inline" }}>{t("worksheet:worksheet_paused")}</h6>
      </div>
      <div className={classes.pausedWorksheetBody}>
        <EmptyState
          dsVersion="2.0"
          illustration={PausedAssignmentIllustration}
        />
        <span className="text-body">
          {t("worksheet:paused_worksheet_description")}
        </span>
      </div>
    </UIModal>
  );
};

//* MessagingPane component for student assignment messaging
const MessagingPane = ({
  handleMessagingToggleIconKeyDown,
  closeMessagingPane,
  conversationId,
  assessmentTitle,
  studentAssignmentId,
  isStudentEvaluationEnabled,
  assignmentId,
  isMediaRestricted,
}) => {
  return (
    <UIModal
      isOpen={true}
      overlayContainer={classes.messagingPaneOverlay}
      modalContent={classes.messagingPaneContent}
    >
      <div className={classes.messagingPane}>
        <IconButton
          dsVersion="2.0"
          type="outlined"
          variant="neutral"
          onClick={closeMessagingPane}
          icon={<ChevronRightOutlined />}
          className={classes.messagingPaneToggleIcon}
          onKeyDown={handleMessagingToggleIconKeyDown}
        />
        <StudentAssignmentMessaging
          conversationId={conversationId}
          assignmentTitle={assessmentTitle}
          isRemovedCourseForLoggedInUser={false}
          studentAssignmentId={studentAssignmentId}
          activeMessagingPane="ASSIGNMENT_MESSAGE"
          isStudentEvaluationEnabled={isStudentEvaluationEnabled}
          isMediaRestricted={isMediaRestricted}
          shouldRefetchConversationOnEvent={params => {
            return (
              params?.event === "assignmentFeedUpdate" &&
              params?.assignmentId === assignmentId
            );
          }}
        />
      </div>
    </UIModal>
  );
};

/**
 *
 * @param { show }
 * @returns Offline Toast Snackbar Component
 * @description
 * - This component displays a snackbar toast message when the user is offline.
 * - IMPORTANT: This component is only rendered immediately after the user goes offline.
 * - After 15 seconds it is overridden by global snackbar in NetworkStatusV2 component. Then this one is removed from DOM.
 */
const SnackbarOfflineToast = ({ show, t }) => {
  if (!show) return null;
  return (
    <div className={classes.snackbarOfflineContainer}>
      <div className={classes.snackbarOfflineToast}>
        {t("common:trying_to_connect_changes_not_saved")}
      </div>
    </div>
  );
};

const StudentWorksheetSubmission = memo(props => {
  const {
    toggleWorksheetModal,
    assignmentId,
    assessmentId,
    data,
    submissions,
    mode: _mode,
    activeQuestion,
    onSubmitWork,
    setWorksheetWebViewRecentlySubmitted,
    updateActiveQuestion,
    worksheetTitle,
    handleWorksheetAssessmentToolClick,
    setWorksheetContainerRef,
    setScrollContainerRef,
    setQuestionRef,
    worksheetTimerComponent,
    showWorksheetResumeModal,
    toggleWorksheetResumeModal,
    isOnline,
    location,
    totalQuestionAttempted,
    setGlobalTrayEnabledState,
    title,
    studentAssignmentData,
    studentAssignmentId,
    createEntityEventTracking,
    isDeleted,
    goToBack,
    setToastMsg,
    worksheetInstructions,
    showAssessmentToolsInQuestions,
    isQueriesV2Enabled,
    refetchStudentAssignmentQuestionSubmissions,
    refetchStudentAssignmentDetails,
  } = props;
  const [localIsOnline, setLocalIsOnline] = useState(isOnline);
  const mode = isOnline && localIsOnline ? _mode : MODE_STUDENT_VIEW;

  const isStudentWorksheetWebView = isWorksheetWebView();
  const isStudentRevamp = isStudentEndMode(mode);
  const isMobile = isMobileByUserAgent();
  const isInSafeExamBrowser = isSafeExamBrowser();

  const isStudentWorkbookForceViewMode = _.isEqual(
    _.get(location, "query.isStudentWorkbookForceViewMode"),
    "true"
  );

  const { t } = useI18n(["common"]);
  const [showInstructionPane, setShowInstructionPane] = useState(false);
  const [openInstructions, setOpenInstructions] = useState(false);
  const instructionsRef = useRef(null);
  const [isFullScreenLE, setIsFullScreenLE] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showWorksheetLockedModal, setShowWorksheetLockedModal] = useState(
    false
  );
  const [showMessagingPane, setShowMessagingPane] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  const assessmentTitle = worksheetTitle || title;

  const {
    isEvaluated,
    isSubmitted,
    isSharedWithStudent,
    isEvaluationApplicable,
    isStudentEvaluationEnabled,
    isTeacherEvaluationEnabled,
    isStudentSubmissionEnabled,
    events,
    course,
    isSubmissionStateLocked,
    isTimedTestEnabled,
    isSafeBrowserEnabled,
    isLockScreenEnabled,
    isWorksheetEvaluated,
    isTestEnded: _isTestEnded,
    showWorksheetAssessmentTool,
    timerComponentProps,
    isOnScreenCalculator,
    ibPYPElementRatings,
    isWorksheetPaused,
  } = getAssignmentData({ studentAssignmentData, mode });
  const previousIsSubmittedRef = useRef(isSubmitted);

  // Extract messaging data
  const conversation = _.get(studentAssignmentData, "conversation", {});
  const conversationId = _.get(conversation, "id", "");
  const unreadMessageCount = _.get(conversation, "unreadMessageCount") || 0;

  const isInitialMount = useRef(true);
  const lastEvent = _.last(events) || {};
  const isLastEventTypePause = _.get(lastEvent, "eventType", "") === "PAUSE";

  const draggableRef = useRef(null);

  const { position, handleMouseDown, handleTouchStart } = useDrag({
    ref: draggableRef,
    isStudentWorksheetWebView,
  });

  const canShowResumeModal =
    isLockScreenEnabled || (isStudentWorksheetWebView && isSafeBrowserEnabled);

  const { isReopened, startTime } = getStudentWorksheetDataFromEvents(events);
  const isTestEnded = isReopened ? false : _isTestEnded;

  // * Checking flag and mode to prevent text pasting in student attempt mode.
  const shouldPreventCopyPaste =
    isLockScreenEnabled &&
    mode === MODE_STUDENT_ATTEMPT &&
    getIsStudentCanCopyPasteInWorksheet();

  // * Disabling clipboard copy paste functionality based on the flag and mode.
  useDisableClipboard(shouldPreventCopyPaste, null);

  // * Refetch student assignment data when network comes back online
  const debounceRefetchStudentAssignmentQuestionSubmissions = useMemo(
    () =>
      _.debounce(async () => {
        if (_.isFunction(refetchStudentAssignmentQuestionSubmissions)) {
          /**
           * When internet is back
           * - Set to view mode, refetch and then set back to edit mode
           * - Changing view mode to reset any local cache data which might have been changed during offline mode
           * - Refetch assignment details in parallel so the assignment-level
           *   state stays in sync with the submissions after offline changes
           */
          setLocalIsOnline(false);
          try {
            await Promise.all([
              refetchStudentAssignmentQuestionSubmissions(),
              _.isFunction(refetchStudentAssignmentDetails)
                ? refetchStudentAssignmentDetails()
                : Promise.resolve(),
            ]);
          } finally {
            setLocalIsOnline(true);
          }
        }
      }, 1000),
    [
      refetchStudentAssignmentDetails,
      refetchStudentAssignmentQuestionSubmissions,
    ]
  );

  // * Handler for network status change
  const onNetworkChange = useCallback(
    online => {
      if (online) debounceRefetchStudentAssignmentQuestionSubmissions();
      else setLocalIsOnline(false);
    },
    [setLocalIsOnline, debounceRefetchStudentAssignmentQuestionSubmissions]
  );

  /**
   * For case when the student is in view mode and the worksheet is reopened (doesn't have start event after reopen)
   * In this case we need to redirect the student back to dashboard or send message to parent app to save and exit
   */
  useEffect(() => {
    if (isReopened && _.isEmpty(startTime)) {
      if (isStudentWorksheetWebView) {
        sendMessageToParentApp("SAVE_AND_EXIT");
      } else {
        toggleWorksheetModal(false);
      }
    }
  }, [mode, isReopened, startTime, isStudentWorksheetWebView]);

  // * Closing attempt mode just after submission
  useEffect(() => {
    if (!previousIsSubmittedRef.current && isSubmitted) {
      if (isStudentWorksheetWebView) {
        sendMessageToParentApp("SAVE_AND_EXIT");
      } else {
        toggleWorksheetModal(false);
      }
    }
    previousIsSubmittedRef.current = isSubmitted;
  }, [isSubmitted, mode, isStudentWorksheetWebView]);

  useEffect(() => {
    /**
     * If the assignment is deleted, then show the toast message
     * if assignment is opened on webview, then send the message to parent app else show the toast message
     */
    const checkDeletedAssignment = async () => {
      if (isDeleted) {
        const isDeletedFromServer = await getAssignmentDeletedStatus(
          assignmentId
        );
        if (isDeletedFromServer) {
          goToBack();
          if (isStudentWorksheetWebView) {
            sendMessageToParentApp("SAVE_FAILED");
          } else {
            setToastMsg("toastMsgs:trying_to_access_deleted_post");
          }
        }
      }
    };

    checkDeletedAssignment();
  }, [assignmentId, isDeleted]);

  //* If the worksheet is paused, then close calculator and assessment tool if they are open
  useEffect(() => {
    if (isWorksheetPaused) {
      if (showCalculator) {
        setShowCalculator(false);
      }

      if (showWorksheetAssessmentTool) {
        handleWorksheetAssessmentToolClick(false);
      }
    }
  }, [isWorksheetPaused, showCalculator, showWorksheetAssessmentTool]);

  const getRefetchQueryParams = useCallback(() => {
    const refetchQueryParams = {
      id: studentAssignmentId,
      userFilter: "ALL",
      entityEventsFilter: {
        orderBy: "EVENT_TIME",
        orderByDir: "ASC",
      },
    };
    if (!getIsStudentAssignmentRevampEnabled()) {
      refetchQueryParams.showOnlySubmitted = false;
      refetchQueryParams.userFilter = "SELF";
    }
    return refetchQueryParams;
  }, [studentAssignmentId]);

  const registerTimeTrackingEvent = useCallback(
    async ({
      eventType,
      duration,
      reason,
      shouldStoreInLocalStorage = false,
      shoudCallMutation = true,
      callbackOnError,
    }) => {
      const eventTime = getDateInUtc();
      const params = {
        entityId: studentAssignmentId,
        entityType: "STUDENT_ASSIGNMENT",
        eventTime,
        eventType,
        duration,
        reason,
      };

      const deviceInfo = getDeviceInfo(t);
      const sendDeviceInfo = _.includes(["RESUME", "START"], eventType);
      if (sendDeviceInfo) {
        _.set(params, "metadata.deviceInfo", deviceInfo);
      }

      if (shouldStoreInLocalStorage) {
        registerTimeTrackingEventInLocalStorage({ events: [params] });
      }

      if (shoudCallMutation) {
        if (isStudentWorksheetWebView) {
          createEntityEventTracking({
            ...params,
            callbackOnError,
            queryParams: {
              id: studentAssignmentId,
              userFilter: "ALL",
              entityEventsFilter: {
                orderBy: "EVENT_TIME",
                orderByDir: "ASC",
              },
            },
            isWorksheetWebView: true,
          });
        } else {
          // For web, we are using createEntityEventTracking mutation
          createEntityEventTracking({
            ...params,
            callbackOnError,
            queryParams: getRefetchQueryParams(),
          });
        }
      }
    },
    [studentAssignmentId]
  );

  const handleVisibleChange = () => {
    if (document.visibilityState === "hidden") {
      // If resume modal is already open, then close it and don't create new pause event
      // so that on next resume, it will show total pause time from last pause event
      if (showWorksheetResumeModal) {
        toggleWorksheetResumeModal(false);
        return;
      }

      if (isOnline && !isLastEventTypePause) {
        registerTimeTrackingEvent({
          eventType: "PAUSE",
          shouldStoreInLocalStorage: true,
        });
      }

      return;
    }

    if (isOnline && isLastEventTypePause) {
      if (canShowResumeModal) {
        if (!showWorksheetResumeModal) {
          toggleWorksheetResumeModal(true);
        }
        return;
      }

      registerTimeTrackingEvent({
        eventType: "RESUME",
        shouldStoreInLocalStorage: true,
      });
    }
  };

  const onBlur = () => {
    // If any DOM element is focused or if Keyboard is visible in app, then don't create pause event
    if (document.hasFocus() || isKeyboardVisible) {
      return;
    }

    // For drawing question -> if focus shift to react modal or iframe of workbook
    if (
      _.isEqual(
        _.get(document, "activeElement.id"),
        "workbook-iframe-global"
      ) ||
      document?.activeElement?.classList.contains("ReactModal__Body--open")
    )
      return;

    if (showWorksheetResumeModal) {
      toggleWorksheetResumeModal(false);
      return;
    }

    if (isOnline && !isLastEventTypePause) {
      registerTimeTrackingEvent({
        eventType: "PAUSE",
        shouldStoreInLocalStorage: true,
      });
    }
  };

  const onFocus = () => {
    if (isOnline && isLastEventTypePause) {
      if (canShowResumeModal) {
        if (!showWorksheetResumeModal) {
          toggleWorksheetResumeModal(true);
        }
        return;
      }

      registerTimeTrackingEvent({
        eventType: "RESUME",
        shouldStoreInLocalStorage: true,
      });
    }
  };

  const handleMessage = event => {
    const data = _.get(event, "data");
    let messageData = data;

    if (typeof data === "string") {
      try {
        messageData = JSON.parse(data);
      } catch (error) {
        // If JSON parsing fails, return early as the message format is invalid
        return;
      }
    }

    const { STATUS, MESSAGE_TOKEN } = messageData;

    if (STATUS === "APP_STATE_CHANGE") {
      if (MESSAGE_TOKEN === "WORKSHEET_PAUSED") {
        // App went to background or inactive - similar to visibilitychange or blur

        if (showWorksheetResumeModal) {
          toggleWorksheetResumeModal(false);
          return;
        }
        if (isOnline && !isLastEventTypePause) {
          registerTimeTrackingEvent({
            eventType: "PAUSE",
            shouldStoreInLocalStorage: true,
          });
        }
      } else if (MESSAGE_TOKEN === "WORKSHEET_RESUMED") {
        // App became active again - similar to focus
        if (isOnline && isLastEventTypePause) {
          if (canShowResumeModal) {
            if (!showWorksheetResumeModal) {
              toggleWorksheetResumeModal(true);
            }
          } else {
            registerTimeTrackingEvent({
              eventType: "RESUME",
              shouldStoreInLocalStorage: true,
            });
          }
        }
      }
    } else if (STATUS === "KEYBOARD_STATE_CHANGE") {
      if (MESSAGE_TOKEN === "KEYBOARD_SHOWN") {
        setIsKeyboardVisible(true);
      } else if (MESSAGE_TOKEN === "KEYBOARD_HIDDEN") {
        setIsKeyboardVisible(false);
      }
    }
  };

  useEffect(() => {
    if (isStudentWorksheetWebView) {
      setGlobalTrayEnabledState(false);
    }
  }, []);

  useEffect(() => {
    if (mode === MODE_STUDENT_ATTEMPT && isTimedTestEnabled && !isTestEnded) {
      if (isStudentWorksheetWebView) {
        // For React Native WebView environment
        // Listen for messages from the parent app to handle visibility/focus changes
        window.addEventListener("message", handleMessage);
      }

      document.addEventListener("visibilitychange", handleVisibleChange);
      window.addEventListener("blur", onBlur);
      window.addEventListener("focus", onFocus);
    }

    return () => {
      // Clean up all event listeners
      document.removeEventListener("visibilitychange", handleVisibleChange);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      // For React Native WebView environment
      if (isStudentWorksheetWebView) {
        window.removeEventListener("message", handleMessage);
      }
    };
  }, [
    isTestEnded,
    showWorksheetResumeModal,
    isOnline,
    lastEvent,
    isKeyboardVisible,
  ]);

  useEffect(() => {
    if (
      !(mode === MODE_STUDENT_ATTEMPT && isTimedTestEnabled && !isTestEnded)
    ) {
      return;
    }

    if (!isOnline) {
      if (document.visibilityState !== "hidden" && document.hasFocus()) {
        if (showWorksheetResumeModal) {
          toggleWorksheetResumeModal(false);
          return;
        }

        if (!isLastEventTypePause) {
          registerTimeTrackingEvent({
            eventType: "PAUSE",
            shouldStoreInLocalStorage: true,
            shouldCallMutation: false,
          });
        }
      }
      return;
    }

    // On component mount do not call resume event if user is online
    if (isInitialMount?.current) {
      isInitialMount.current = false;
      return;
    }

    if (
      !isInitialMount?.current &&
      document.visibilityState !== "hidden" &&
      document.hasFocus() &&
      isLastEventTypePause
    ) {
      if (canShowResumeModal && !showWorksheetResumeModal) {
        toggleWorksheetResumeModal(true);
        return;
      }

      registerTimeTrackingEvent({
        eventType: "RESUME",
        shouldStoreInLocalStorage: true,
      });
    }
  }, [isOnline, isTestEnded]);

  const browserBackCallBack = () => {
    window.history.pushState(null, document.title, window.location.href);
  };

  useEffect(() => {
    if (
      !(
        mode === MODE_STUDENT_ATTEMPT &&
        isTimedTestEnabled &&
        isLockScreenEnabled &&
        !isTestEnded
      )
    ) {
      return;
    }

    window.history.pushState(null, document.title, window.location.href);

    window.addEventListener("popstate", browserBackCallBack);

    return () => {
      window.removeEventListener("popstate", browserBackCallBack);

      // remove top most item from history stack else there will be two same urls at top of history stack
      window.history.back();
    };
  }, []);

  useEffect(() => {
    const questionId = _.get(_.find(options, { isQuestion: true }), "id");

    if (_.isEmpty(activeQuestion) && questionId) {
      updateActiveQuestion(questionId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDidUpdate(() => {
    if (
      isSubmissionStateLocked &&
      !isTimedTestEnabled &&
      mode === MODE_STUDENT_VIEW
    ) {
      setShowWorksheetLockedModal(true);
    }

    if (mode === MODE_STUDENT_ATTEMPT && showWorksheetLockedModal) {
      setShowWorksheetLockedModal(false);
    }
  }, [
    showWorksheetLockedModal,
    mode,
    isSubmissionStateLocked,
    isTimedTestEnabled,
  ]);

  const scoreValue = getScoreValueElementRatings({
    ibPYPElementRatings,
  });

  const memoizedOptions = useMemo(() => {
    //WORKSHEET_RESTRUCTURING: Use getSideBarOptionsV2 when queriesV2 is enabled
    const _getSideBarOptions = isQueriesV2Enabled
      ? getSideBarOptionsV2
      : getSideBarOptions;
    return _getSideBarOptions({
      data,
      submissions,
      mode,
      t,
      scoreValue,
    });
  }, [data, submissions, mode, t, scoreValue, isQueriesV2Enabled]);

  const { questionCount, totalMarks, totalScore, options } = memoizedOptions;

  const questionAttemptedDependency = useMemo(() => {
    const submissionDeps = _.map(
      submissions,
      submission => `${submission.id}-${submission.isQuestionAttempted}`
    );
    return _.uniq(submissionDeps).join("|");
  }, [submissions]);

  /**
   * Throttled function to send attempted question updates
   * Next event will be sent after 5 second of previous event, if there is any change in attempted question in submissions
   */
  const throttledSendEventRef = useRef(
    _.throttle(
      ({ assignmentId: _assignmentId, submissions: _submissions, t: _t }) => {
        const attemptedQuestion = _.reduce(
          _submissions,
          (questionMap, submission) => {
            const questionId = _.get(submission, "question.id");
            questionMap[questionId] = {
              isAttempted: _.get(submission, "isQuestionAttempted"),
            };
            return questionMap;
          },
          {}
        );
        sendWorksheetMonitoringEvent(
          _assignmentId,
          "QUESTION_ATTEMPTED_AND_DEVICE_INFO",
          { attemptedQuestion, deviceInfo: getDeviceInfo(_t) }
        );
      },
      5000,
      { leading: true, trailing: true }
    )
  );

  useEffect(() => {
    return () => throttledSendEventRef.current.cancel();
  }, []);

  useEffect(() => {
    if (assessmentId && mode === MODE_STUDENT_ATTEMPT) {
      throttledSendEventRef.current({ assignmentId, submissions, t });
    }
  }, [mode, assignmentId, questionAttemptedDependency]);

  const toggleInstructionsPane = useCallback(() => {
    setShowInstructionPane(prevState => !prevState);
  }, []);

  const toggleFullscreenLE = useCallback(() => {
    setIsFullScreenLE(prevState => !prevState);
  }, []);

  const handleSidebarInstructionsClick = useCallback(() => {
    // Added this because if i don't toggle accordian will not recognise state change, and will be stuck if user manaully closes it
    setOpenInstructions(!openInstructions);

    if (!_.isEmpty(worksheetInstructions)) {
      // bump signal to expand accordion and scroll into view
      // if ref already available, scroll immediately as well
      instructionsRef.current?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [openInstructions, worksheetInstructions, setOpenInstructions]);

  const closeMessagingPane = () => {
    setShowMessagingPane(false);
  };

  const handleMessagingToggleIconKeyDown = e => {
    onEnterOrSpaceKeyDown(e, closeMessagingPane);
  };

  const toggleWorksheetLockedModal = () => {
    setShowWorksheetLockedModal(false);
    toggleWorksheetModal(false);
  };

  const headerLabel =
    mode == MODE_TEACHER_PREVIEW
      ? t("worksheet:preview_of_with_label", {
          label: assessmentTitle,
        })
      : assessmentTitle;

  const renderWorksheetLockedModal = () => {
    return (
      <Modal
        width={"440px"}
        height={"max-content"}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
        onClose={toggleWorksheetLockedModal}
        testId="worksheet-studentWorksheetSubmission-closeWorksheetLockedModal"
      >
        <Modal.Header title={t("classRoom:submission_closed")} />
        <Modal.Body>
          <div className={classes.lockedMessage}>
            {t("classRoom:worksheet_submission_closed_message")}
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  const instructionContainer = classNames({
    [classes.instructionsContainer]: isStudentRevamp,
  });

  return (
    <div
      className={classes.parentContainer}
      ref={setWorksheetContainerRef}
      id={WORKSHEET_ELEMENT_IDS.WORKSHEET_STUDENT_SUBMISSION_CONTAINER_ID}
    >
      <Header
        mode={mode}
        contentType={"ASSESSMENT"}
        headerLabel={headerLabel}
        totalMarks={totalMarks}
        totalScore={totalScore}
        onSubmitWork={onSubmitWork}
        setWorksheetWebViewRecentlySubmitted={
          setWorksheetWebViewRecentlySubmitted
        }
        toggleWorksheetModal={toggleWorksheetModal}
        toggleInstructionsPane={toggleInstructionsPane}
        setIsFullScreenLE={setIsFullScreenLE}
        handleWorksheetAssessmentToolClick={handleWorksheetAssessmentToolClick}
        showWorksheetAssessmentTool={showWorksheetAssessmentTool}
        worksheetTimerComponent={worksheetTimerComponent}
        registerTimeTrackingEvent={registerTimeTrackingEvent}
        isTimedTestEnabled={isTimedTestEnabled}
        isLockScreenEnabled={isLockScreenEnabled}
        isSafeBrowserEnabled={isSafeBrowserEnabled}
        isTestEnded={isTestEnded}
        t={t}
        setShowCalculator={setShowCalculator}
        showCalculator={showCalculator}
        isOnScreenCalculator={isOnScreenCalculator}
        timerComponentProps={timerComponentProps}
        closeSubmissionDate={
          studentAssignmentData?.assignment?.closeSubmissionDate
        }
        setShowMessagingPane={setShowMessagingPane}
        unreadMessageCount={unreadMessageCount}
        isSubmitted={isSubmitted}
      />
      <div className={classes.container}>
        <div className={classes.content} ref={setScrollContainerRef}>
          {!_.isEmpty(worksheetInstructions) && (
            <div className={instructionContainer}>
              <StudentWorksheetInstructions
                value={worksheetInstructions}
                t={t}
                openInstructions={openInstructions}
                setRef={ref => (instructionsRef.current = ref)}
                isStudentEndMode={isStudentEndMode(mode)}
              />
            </div>
          )}
          <SectionFeed
            sections={data}
            mode={mode}
            submissions={submissions}
            isEvaluationApplicable={getIsEvaluationApplicable(
              isEvaluationApplicable
            )}
            isSharedWithStudent={isSharedWithStudent}
            isStudentEvaluationEnabled={false} // Student evaluation of assessment tools is not applicable at question level
            isTeacherEvaluationEnabled={isTeacherEvaluationEnabled}
            isStudentSubmissionEnabled={isStudentSubmissionEnabled}
            isEvaluated={isEvaluated}
            isSubmitted={isSubmitted}
            isWorksheetEvaluated={isWorksheetEvaluated}
            setQuestionRef={setQuestionRef}
            isStudentWorkbookForceViewMode={isStudentWorkbookForceViewMode}
            assignmentId={assignmentId}
            showAssessmentToolsInQuestions={showAssessmentToolsInQuestions}
            studentAssignmentId={studentAssignmentId}
            isQueriesV2Enabled={isQueriesV2Enabled}
            isLockScreenEnabled={isLockScreenEnabled}
          />
        </div>

        {(isStudentWorksheetWebView && !isStudentEndMode(mode)) ||
        isMobile ? null : (
          <div className={classes.rightPanel}>
            <SideBar
              mode={mode}
              questionCount={questionCount}
              attemptedCount={totalQuestionAttempted}
              totalMarks={totalMarks}
              options={options}
              worksheetInstructions={worksheetInstructions}
              onInstructionsClick={handleSidebarInstructionsClick}
            />
          </div>
        )}
      </div>

      {showInstructionPane ? (
        <div className={classes.instruction} style={elevations["elevation-2B"]}>
          <CompactLEPreview
            assessmentId={assessmentId}
            assignmentId={assignmentId}
            portalType={"PLANNER"}
            showAttachmentItemV3={true}
            showCloseButton={true}
            showFullScreenButton={true}
            onCloseClick={toggleInstructionsPane}
            attachmentItemHeight={100}
            isFullScreenLE={isFullScreenLE}
            toggleFullscreenLE={toggleFullscreenLE}
            showEmptyState={true}
            isStudentWorksheetWebView={isStudentWorksheetWebView}
            headerLeftComp={
              <HeaderV2
                currentStep={"LEPreview"}
                headerLabel={assessmentTitle}
                genericTags={_.get(course, "genericTags", [])}
              />
            }
          />
        </div>
      ) : null}
      {showCalculator
        ? createPortal(
            <div
              className={classes.draggable}
              ref={draggableRef}
              style={{
                top: position.y,
                left: position.x,
              }}
            >
              <div className={classes.calculator}>
                <Calculator
                  handleMouseDown={handleMouseDown}
                  setShowCalculator={setShowCalculator}
                  handleTouchStart={handleTouchStart}
                  isStudentWorksheetWebView={isStudentWorksheetWebView}
                  worksheetId={assessmentId}
                  allowCopyPaste={!shouldPreventCopyPaste}
                />
              </div>
            </div>,
            document.body
          )
        : null}
      {showWorksheetLockedModal ? renderWorksheetLockedModal() : null}
      {/* Messaging Pane - shown as overlay when active */}
      {showMessagingPane && (
        <MessagingPane
          handleMessagingToggleIconKeyDown={handleMessagingToggleIconKeyDown}
          closeMessagingPane={closeMessagingPane}
          conversationId={conversationId}
          assessmentTitle={assessmentTitle}
          studentAssignmentId={studentAssignmentId}
          isStudentEvaluationEnabled={isStudentEvaluationEnabled}
          assignmentId={assignmentId}
          isMediaRestricted={isInSafeExamBrowser}
        />
      )}
      {isWorksheetPaused && mode === MODE_STUDENT_ATTEMPT && (
        <PausedWorksheetModal isMessagePaneActive={showMessagingPane} />
      )}
      <Detector
        polling={false}
        render={() => null}
        onChange={onNetworkChange}
      />
      <SnackbarOfflineToast show={!localIsOnline && isOnline} t={t} />
    </div>
  );
});

const mapStateToProps = (state, ownProps) => {
  const { assessmentId, assignmentId } = ownProps;
  const activeQuestion = _.get(state, "worksheet.activeQuestion");
  let worksheetTitle = "";

  const userInfo = getUserInfo();
  const userType = _.get(userInfo, "user_type");
  const organizationId = _.get(userInfo, "org_id");
  const academicYearId = getSelectedAcademicYearId(state);
  const isStudentAssignmentRevampEnabled = getIsStudentAssignmentRevampEnabled();
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  if (_.isEqual(userType, "staff")) {
    const assessmentDetails = getAssessmentDetailsFromCache({
      id: assessmentId,
      collaboratorsFilter,
    });
    worksheetTitle = _.get(
      _.find(_.get(assessmentDetails, "allFields", []), {
        uid: FIELD_UID_TITLE,
      }),
      "value"
    );
  }

  const pusherData = { organizationId, assessmentId: assignmentId };

  return {
    activeQuestion,
    isOnline: getIsUserOnline(state),
    worksheetTitle,
    userType,
    isData: true,
    isLoading: false,
    isStudentAssignmentRevampEnabled,
    pusherData,
  };
};

const mapActionCreators = {
  goToBack,
  setToastMsg,
  updateActiveQuestion,
  setGlobalTrayEnabledState,
  createEntityEventTracking,
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    onPusherEventTriggered: eventData => {
      if (
        eventData?.assignmentId === ownProps?.assignmentId &&
        _.get(eventData, "event") === "assignmentFeedUpdate"
      ) {
        setTimeout(() => {
          // * Allowing to fetch data and settle down before showing the toast
          dispatchStudentMonitoringEventToast({
            eventData,
            t: ownProps?.t,
            setToastMsg: dispatchProps?.setToastMsg,
          });
        }, 1000);
      }
    },
  };
};

StudentWorksheetSubmission.displayName = "StudentWorksheetSubmission";

StudentWorksheetSubmission.propTypes = {
  worksheetInstructions: PropTypes.string,
  showAssessmentToolsInQuestions: PropTypes.bool,
  isQueriesV2Enabled: PropTypes.bool,
};

StudentWorksheetSubmission.defaultProps = {
  showAssessmentToolsInQuestions: false,
  isQueriesV2Enabled: false,
};

export default compose(
  connect(mapStateToProps, mapActionCreators, mergeProps),
  withPusherBind(["assignmentFeedUpdate"]),
  getStudentQuestionDetailsEnhancer(),
  getStudentAssignmentDetailsEnhancer(),
  getStudentAssignmentEnhancer(),
  getStudentQuestionDetailsEnhancerV2(),
  withRouter,
  withLoader,
  withPresenceChannel("worksheet")
)(StudentWorksheetSubmission);
